// material-ui
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

// project imports
import Navigation from './Navigation';
import SimpleBar from 'components/third-party/SimpleBar';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import Permission from 'components/Permission';
import { ThemeMode } from 'config';
import SessionTimer from 'components/SessionTimer';
import Notification from 'layout/Dashboard/Header/HeaderContent/Notification';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { SidebarContent, SidebarFooter, SidebarMenu, SidebarMenuItem, SidebarMenuButton } from '../SidebarComponents';

// assets
import UserOutlined from '@ant-design/icons/UserOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import VoiceIcon from 'components/icons/VoiceIcon';
import CommentOutlined from '@ant-design/icons/CommentOutlined';
import SafetyOutlined from '@ant-design/icons/SafetyOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import ApiOutlined from '@ant-design/icons/ApiOutlined';
import DollarOutlined from '@ant-design/icons/DollarOutlined';
import WalletOutlined from '@ant-design/icons/WalletOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import MoonOutlined from '@ant-design/icons/MoonOutlined';
import SunOutlined from '@ant-design/icons/SunOutlined';
import { FormattedMessage, useIntl } from 'react-intl';

// ==============================|| DRAWER CONTENT ||============================== //

export default function DrawerContent() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, expiresIn } = useAuth();
  const { mode, onChangeMode } = useConfig();
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const isDarkMode = mode === ThemeMode.DARK;

  // Verificar se a rota atual corresponde aos caminhos dos itens do footer
  const isAccountSelected = !!matchPath({ path: '/account', end: false }, pathname);
  const isDevicesSelected = !!matchPath({ path: '/devices', end: false }, pathname);
  const isBlocksSelected = !!matchPath({ path: '/blocks', end: false }, pathname);
  const isSensitiveFieldsSelected = !!matchPath({ path: '/sensitive-fields', end: false }, pathname);
  const isMcpIntegrationsSelected = !!matchPath({ path: '/settings/integrations', end: false }, pathname);
  const isAiBudgetSelected = !!matchPath({ path: '/ai-budget', end: false }, pathname);
  const isMyAiBudgetSelected = !!matchPath({ path: '/my-ai-budget', end: false }, pathname);
  const isPaymentPackagesSelected = !!matchPath({ path: '/creditos', end: true }, pathname);
  const isPaymentHistorySelected = !!matchPath({ path: '/creditos/historico', end: false }, pathname);
  const isPaymentAdminPackagesSelected = !!matchPath({ path: '/admin/pagamentos/pacotes', end: false }, pathname);
  const isPaymentAdminOrdersSelected = !!matchPath({ path: '/admin/pagamentos/pedidos', end: false }, pathname);
  const isPaymentsSelected =
    isPaymentPackagesSelected ||
    isPaymentHistorySelected ||
    isPaymentAdminPackagesSelected ||
    isPaymentAdminOrdersSelected;
  const isSettingsSelected =
    isDevicesSelected ||
    isBlocksSelected ||
    isSensitiveFieldsSelected ||
    isMcpIntegrationsSelected ||
    isAiBudgetSelected ||
    isMyAiBudgetSelected;

  // Abrir o menu "Ajustes" automaticamente quando um de seus filhos estiver selecionado
  useEffect(() => {
    if (isSettingsSelected && !settingsOpen) {
      setSettingsOpen(true);
    }
  }, [isSettingsSelected, settingsOpen]);

  useEffect(() => {
    if (isPaymentsSelected && !paymentsOpen) {
      setPaymentsOpen(true);
    }
  }, [isPaymentsSelected, paymentsOpen]);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (downLG) {
      handlerDrawerOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (err) {
      console.error(err);
    }
  };

  const handleSettingsToggle = () => {
    setSettingsOpen(!settingsOpen);
  };

  const handlePaymentsToggle = () => {
    setPaymentsOpen(!paymentsOpen);
  };

  return (
    <SimpleBar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0
        },
        '& .simplebar-wrapper': {
          height: '100%'
        },
        '& .simplebar-scrollbar': {
          zIndex: 2
        }
      }}
    >
      <SidebarContent>
        <Navigation />
      </SidebarContent>

      <SidebarFooter>
        {!downLG && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: drawerOpen ? 'space-between' : 'center',
              gap: 1,
              px: drawerOpen ? 1 : 0,
              pb: 1,
              mb: 1,
              borderBottom: '1px dashed',
              borderColor: 'divider'
            }}
          >
            {drawerOpen && expiresIn && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SessionTimer expiresIn={expiresIn} variant="compact" />
              </Box>
            )}
            <Box sx={{ flexShrink: 0 }}>
              <Notification />
            </Box>
          </Box>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton icon={<UserOutlined />} selected={isAccountSelected} onClick={() => handleNavigation('/account')}>
              Perfil
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <ListItemButton
              selected={isSettingsSelected}
              onClick={handleSettingsToggle}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                ...(!drawerOpen && {
                  justifyContent: 'center',
                  px: 1.5
                }),
                ...(drawerOpen && {
                  '&.Mui-selected': {
                    backgroundColor: 'primary.lighter',
                    borderRight: '0px solid',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.lighter'
                    }
                  }
                }),
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: drawerOpen ? 40 : 'auto',
                  justifyContent: drawerOpen ? 'flex-start' : 'center',
                  color: isSettingsSelected ? 'primary.main' : 'text.secondary'
                }}
              >
                <SettingOutlined />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>Ajustes</span>
                      {settingsOpen ? (
                        <DownOutlined style={{ fontSize: '0.875rem' }} />
                      ) : (
                        <RightOutlined style={{ fontSize: '0.875rem' }} />
                      )}
                    </Box>
                  }
                />
              )}
            </ListItemButton>
          </SidebarMenuItem>

          <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
            <Box
              sx={{
                position: 'relative',
                ...(drawerOpen && {
                  pl: 4,
                  ml: 1
                })
              }}
            >
              {drawerOpen && (
                <Box
                  sx={(theme) => ({
                    position: 'absolute',
                    left: '50px',
                    top: theme.spacing(3.5),
                    bottom: theme.spacing(3.5),
                    width: '1px',
                    borderLeft: `1px dashed ${theme.palette.divider}`
                  })}
                />
              )}
              <List component="div" disablePadding>
                <SidebarMenuItem>
                  <Tooltip title={intl.formatMessage({ id: 'settings-dark-mode' })} placement="right" disableInteractive>
                    <ListItemButton
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('.MuiSwitch-root')) return;
                        onChangeMode(isDarkMode ? ThemeMode.LIGHT : ThemeMode.DARK);
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        ...(!drawerOpen && {
                          justifyContent: 'center',
                          px: 1.5
                        }),
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: drawerOpen ? 40 : 'auto',
                          justifyContent: drawerOpen ? 'flex-start' : 'center',
                          color: 'text.secondary'
                        }}
                      >
                        {isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                      </ListItemIcon>
                      {drawerOpen && (
                        <>
                          <ListItemText
                            primary={
                              <Typography component="span" variant="body2" color="text.primary">
                                <FormattedMessage id="settings-dark-mode" />
                              </Typography>
                            }
                          />
                          <Switch
                            size="small"
                            checked={isDarkMode}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(_, checked) => onChangeMode(checked ? ThemeMode.DARK : ThemeMode.LIGHT)}
                            inputProps={{
                              'aria-label': intl.formatMessage({ id: 'settings-dark-mode' })
                            }}
                          />
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </SidebarMenuItem>

                <Permission rule="access-keys.read">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      icon={<CommentOutlined />}
                      selected={isDevicesSelected}
                      onClick={() => handleNavigation('/devices')}
                    >
                      <FormattedMessage id="devices-center" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </Permission>

                <Permission rule="users.block_access">
                  <SidebarMenuItem>
                    <SidebarMenuButton icon={<SafetyOutlined />} selected={isBlocksSelected} onClick={() => handleNavigation('/blocks')}>
                      <FormattedMessage id="account-blocks" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </Permission>

                <Permission rule="privacy.sensitive-fields.read">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      icon={<SafetyOutlined />}
                      selected={isSensitiveFieldsSelected}
                      onClick={() => handleNavigation('/sensitive-fields')}
                    >
                      Dados Sensíveis
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </Permission>

                <Permission rule="ai_chat.assistants.read">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      icon={<ApiOutlined />}
                      selected={isMcpIntegrationsSelected}
                      onClick={() => handleNavigation('/settings/integrations')}
                    >
                      <FormattedMessage id="ai-chat-mcp-integrations-menu" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </Permission>

                <Permission rule="ai.budget.me">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      icon={<DollarOutlined />}
                      selected={isMyAiBudgetSelected}
                      onClick={() => handleNavigation('/my-ai-budget')}
                    >
                      <FormattedMessage id="ai-budget-me" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </Permission>

                <Permission rule="ai.budget.manage">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      icon={<DollarOutlined />}
                      selected={isAiBudgetSelected}
                      onClick={() => handleNavigation('/ai-budget')}
                    >
                      <FormattedMessage id="ai-budget" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </Permission>
              </List>
            </Box>
          </Collapse>

          <Permission rule={['payments.orders.create', 'payments.orders.read', 'payments.packages.manage']}>
            <SidebarMenuItem>
              <ListItemButton
                selected={isPaymentsSelected}
                onClick={handlePaymentsToggle}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  ...(!drawerOpen && {
                    justifyContent: 'center',
                    px: 1.5
                  }),
                  ...(drawerOpen && {
                    '&.Mui-selected': {
                      backgroundColor: 'primary.lighter',
                      borderRight: '0px solid',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.lighter'
                      }
                    }
                  }),
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: drawerOpen ? 40 : 'auto',
                    justifyContent: drawerOpen ? 'flex-start' : 'center',
                    color: isPaymentsSelected ? 'primary.main' : 'text.secondary'
                  }}
                >
                  <WalletOutlined />
                </ListItemIcon>
                {drawerOpen && (
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <FormattedMessage id="payments" />
                        {paymentsOpen ? (
                          <DownOutlined style={{ fontSize: '0.875rem' }} />
                        ) : (
                          <RightOutlined style={{ fontSize: '0.875rem' }} />
                        )}
                      </Box>
                    }
                  />
                )}
              </ListItemButton>
            </SidebarMenuItem>

            <Collapse in={paymentsOpen} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  position: 'relative',
                  ...(drawerOpen && {
                    pl: 4,
                    ml: 1
                  })
                }}
              >
                {drawerOpen && (
                  <Box
                    sx={(theme) => ({
                      position: 'absolute',
                      left: '50px',
                      top: theme.spacing(3.5),
                      bottom: theme.spacing(3.5),
                      width: '1px',
                      borderLeft: `1px dashed ${theme.palette.divider}`
                    })}
                  />
                )}
                <List component="div" disablePadding>
                  <Permission rule="payments.orders.create">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        icon={<DollarOutlined />}
                        selected={isPaymentPackagesSelected}
                        onClick={() => handleNavigation('/creditos')}
                      >
                        <FormattedMessage id="payment-packages" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </Permission>

                  <Permission rule="payments.orders.read">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        icon={<FileTextOutlined />}
                        selected={isPaymentHistorySelected}
                        onClick={() => handleNavigation('/creditos/historico')}
                      >
                        <FormattedMessage id="payment-history" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </Permission>

                  <Permission rule="payments.packages.manage">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        icon={<WalletOutlined />}
                        selected={isPaymentAdminPackagesSelected}
                        onClick={() => handleNavigation('/admin/pagamentos/pacotes')}
                      >
                        <FormattedMessage id="payment-admin-packages" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </Permission>

                  <Permission rule="payments.packages.manage">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        icon={<FileOutlined />}
                        selected={isPaymentAdminOrdersSelected}
                        onClick={() => handleNavigation('/admin/pagamentos/pedidos')}
                      >
                        <FormattedMessage id="payment-admin-orders" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </Permission>
                </List>
              </Box>
            </Collapse>
          </Permission>

          <SidebarMenuItem>
            <SidebarMenuButton icon={<LogoutOutlined />} onClick={handleLogout}>
              Logout
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </SimpleBar>
  );
}
